/**
 * Notifications — F4 full list page.
 *
 * Filters: All / Unread / Read. Click row to mark read + navigate to action_url.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TYPE_BADGES = {
    mention:  "bg-violet-50 text-violet-700 border-violet-200",
    info:     "bg-sky-50 text-sky-700 border-sky-200",
    warning:  "bg-amber-50 text-amber-700 border-amber-200",
    task:     "bg-emerald-50 text-emerald-700 border-emerald-200",
    workflow: "bg-cyan-50 text-cyan-700 border-cyan-200",
    payment:  "bg-rose-50 text-rose-700 border-rose-200",
    system:   "bg-gray-50 text-gray-700 border-gray-200",
};

const FILTERS = [
    { key: "all",    label: "All" },
    { key: "unread", label: "Unread" },
    { key: "read",   label: "Read" },
];

export default function Notifications() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const params = new URLSearchParams({ limit: "200" });
            if (filter === "unread") params.set("unread_only", "true");
            if (q.trim()) params.set("q", q.trim());
            const { data } = await api.get(`/notifications?${params.toString()}`);
            let list = data.items || [];
            if (filter === "read") list = list.filter(n => n.read_at);
            setItems(list);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }, [filter, q]);

    useEffect(() => { load(); }, [load]);

    const markRead = async (n) => {
        if (n.read_at) return;
        try {
            await api.patch(`/notifications/${n.id}/read`);
            setItems(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const open = async (n) => {
        await markRead(n);
        if (n.action_url) navigate(n.action_url);
    };

    const markAll = async () => {
        try {
            const r = await api.post("/notifications/mark-all-read");
            toast.success(`Marked ${r.data.updated} read`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-4" data-testid="notifications-page">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-muted-foreground" />
                    <h1 className="text-xl font-semibold">Notifications</h1>
                </div>
                <Button size="sm" variant="outline" onClick={markAll} data-testid="notifications-page-mark-all">Mark all read</Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                {FILTERS.map(f => (
                    <button
                        key={f.key}
                        onClick={() => setFilter(f.key)}
                        className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${filter === f.key ? "bg-primary text-white border-primary" : "bg-white text-gray-700 border-border hover:bg-gray-50"}`}
                        data-testid={`notifications-filter-${f.key}`}
                    >{f.label}</button>
                ))}
                <div className="relative ml-2 flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title, message, case…" className="h-8 pl-8" data-testid="notifications-search" />
                </div>
            </div>

            <div className="rounded-md border border-border bg-white divide-y divide-border">
                {busy && items.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground" data-testid="notifications-page-empty">No notifications</div>
                ) : items.map(n => (
                    <div
                        key={n.id}
                        className={`p-3 flex items-start gap-3 ${n.read_at ? "" : "bg-blue-50/40"}`}
                        data-testid={`notif-row-${n.id}`}
                    >
                        {!n.read_at && <span className="mt-1.5 h-2 w-2 rounded-full bg-rose-500 shrink-0" />}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold">{n.title}</span>
                                <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${TYPE_BADGES[n.notification_type] || TYPE_BADGES.info}`}>{n.notification_type}</span>
                                {n.priority && n.priority !== "normal" && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200">{n.priority}</span>}
                            </div>
                            {n.message && <div className="text-sm text-gray-700 mt-0.5">{n.message}</div>}
                            <div className="text-[11px] text-muted-foreground mt-1">
                                {new Date(n.created_at).toLocaleString()}
                                {(n.lead_name || n.client_name) && <> · {n.lead_name ? `Lead: ${n.lead_name}` : `Client: ${n.client_name}`}</>}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {n.action_url && (
                                <Button size="sm" variant="outline" onClick={() => open(n)} data-testid={`notif-view-${n.id}`}>View case</Button>
                            )}
                            {!n.read_at && (
                                <Button size="sm" variant="ghost" onClick={() => markRead(n)} data-testid={`notif-markread-${n.id}`}>Mark read</Button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
